import { PAGE_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import { getI18nService } from '@rendora/common/services/i18n';
import IconAccountFill from '@rendora/design/assets/account-fill.svg?component';
import IconAccount from '@rendora/design/assets/account.svg?component';
import IconConsumeFill from '@rendora/design/assets/consume-fill.svg?component';
import IconConsume from '@rendora/design/assets/consume.svg?component';
import IconEquityFill from '@rendora/design/assets/equity-fill.svg?component';
import IconEquity from '@rendora/design/assets/equity.svg?component';
import IconOrderFill from '@rendora/design/assets/order-fill.svg?component';
import IconOrder from '@rendora/design/assets/order.svg?component';
import IconOrganizationFill from '@rendora/design/assets/organization-fill.svg?component';
import IconOrganization from '@rendora/design/assets/organization.svg?component';
import { USER_LEVEL_KEY } from '~/api/interface';
import { USER_ADMIN } from '~/constants';
import { PageKey } from '~/constants/page';
import { useDetector } from '~/hooks/useDetector';

export interface Nav {
  name: string;
  route?: string;
  pageKey?: PageKey;
  activePageKeys?: PageKey[];
  link?: string;
  children?: Nav[];
  icon?: string;
  iconActive?: string;
  iconGlobal?: string;
  crown?: boolean;
  dropIcon?: string;
  hidden?: boolean;
  trackEvent?: TRACK_EVENT;
  target?: string;
  description?: string;
}

export const useTopNav = () => {
  const { t: $t } = getI18nService();
  const { hasPagePermission } = usePageAndActionPermissionData();
  const runtimeConfig = useRuntimeConfig();
  const isPrivate = runtimeConfig.public.privatization;

  if (isPrivate) return computed<Nav[]>(() => []);

  return computed<Nav[]>(() => {
    const base = [
      {
        name: $t('chan-pin'),
        // pageKey: PageKey.AI_AVATAR,
        children: [
          {
            route: '/3d-ai-avatar',
            name: $t('3d-shu-zi-ren'),
            pageKey: PageKey.AI_AVATAR,
            activePageKeys: [PageKey.AI_AVATAR, PageKey.AVATAR_LIBRARY],
          },
          {
            name: $t('ai-shi-pin-sheng-cheng'),
            route: '/ai-avatar-video-generation',
            pageKey: PageKey.AI_AVATAR_VIDEO_GENERATION,
          },
          {
            name: $t('ai-yu-yin-sheng-cheng'),
            route: '/ai-voice',
            pageKey: PageKey.AI_VOICE,
          },
          {
            name: $t('ppt-speak'),
            route: '/ppt-speak',
            pageKey: PageKey.PPT_SPEAK,
            hidden: !isChinese(),
          },
          {
            name: $t('ppt-sheng-cheng-shi-pin'),
            route: '/ppt-or-pdf-to-video',
            pageKey: PageKey.PPT_OR_PDF_TO_VIDEO,
            hidden: !isChinese(),
          },
          {
            name: $t('ai-sheng-cheng-ppt'),
            route: '/ai-presentation-generator',
            pageKey: PageKey.AI_PPT,
            hidden: !isChinese(),
          },
          {
            name: $t('shi-pin-chang-jing'),
            route: '/scene',
            pageKey: PageKey.SCENE_INTRODUCTION,
          },
          {
            name: 'API',
            route: '/api',
            pageKey: PageKey.API,
            hidden: !isChinese(),
          },
          {
            name: $t('ai-hu-dong-shu-zi-ren'),
            route: 'https://xingyun3d.com/',
            target: '_blank',
            hidden: !isChinese(),
          },
        ].filter(item => !item.hidden),
      },
      {
        name: $t('zi-yuan-guang-chang'),
        children: [
          {
            route: '/characters',
            name: $t('shu-zi-ren-guang-chang'),
            pageKey: PageKey.CHARACTER,
            activePageKeys: [PageKey.CHARACTER, PageKey.CHARACTER_DETAIL],
          },
          {
            name: $t('zuo-pin-zhong-xin'),
            route: '/template',
            pageKey: PageKey.TEMPLATE,
          },
        ],
      },

      {
        name: $t('ying-yong-chang-jing'),
        pageKey: PageKey.SCENARIOS,
      },
      {
        name: $t('fu-fei-ding-yue'),
        route: '/subscribe',
        pageKey: PageKey.SUBSCRIBE,
        trackEvent: TRACK_EVENT.CLICK_SUBSCRIBE,
      },
      {
        name: $t('mo-fa-xue-yuan'),
        route: '/academy',
        pageKey: PageKey.ACADEMY,
        trackEvent: TRACK_EVENT.CLICK_SCHOOL,
        children: [
          {
            route: isChinese() ? '/category/what-is-youyan-32' : '/academy/1-1',
            name: $t('liao-jie-you-yan'),
          },
          {
            route: '/category/industry-examples-17',
            name: $t('hang-ye-gan-huo'),
            hidden: !isChinese(),
          },
          {
            route: '/customer-stories',
            name: $t('ke-hu-an-li'),
            hidden: !isChinese(),
          },
          {
            route: '/category/live-streaming-case-examples-26',
            name: $t('zhi-bo-fen-xiang'),
            hidden: !isChinese(),
          },
          {
            route: '/tutorial',
            name: $t('cao-zuo-jiao-cheng'),
          },
        ].filter(item => !item.hidden),
      },
      {
        name: $t('bang-zhu-zhong-xin'),
        route: '/help-center',
        pageKey: PageKey.HELP_CENTER,
        icon: 'icon-help-center',
        trackEvent: TRACK_EVENT.CLICK_HELP_CENTER,
        description: $t('zai-zhe-li-kuai-su-cha-zhao-bang-zhu-wen-dang'),
        children: [
          {
            route: isChinese() ? '/help-center/40-135.html' : '/help-center/6-6.html',
            name: $t('ren-shi-you-yan'),
          },
          {
            route: isChinese() ? '/help-center/41-155.html' : '/help-center/9-25.html',
            name: $t('kuai-su-shang-shou'),
          },
          {
            route: isChinese() ? '/help-center/42-137.html' : '/help-center/10-31.html',
            name: $t('shi-yong-zhi-nan'),
          },
          {
            route: isChinese() ? '/help-center/43-146.html' : '/help-center/5-5.html',
            name: $t('chang-jian-wen-ti'),
          },
          {
            route: isChinese() ? '/help-center/44-152.html' : '/help-center/8-13.html',
            name: $t('zui-xin-dong-tai'),
          },
          {
            route: isChinese() ? '/help-center/45-205.html' : '/help-center/11-24.html',
            name: $t('qi-ye-fu-wu'),
          },
        ],
      },
    ] as Nav[];

    return base.filter(item => {
      if (!item) {
        return false;
      }

      if (item.pageKey === PageKey.PRIME) {
        return true;
      }

      if (item.children && item.children.length <= 0) {
        return false;
      }

      // 假如 pageKey 不存在，或者有权限，则返回 true
      return !item.pageKey || hasPagePermission(item.pageKey);
    });
  });
};

export const useHomeNav = () => {
  const { t } = getI18nService();
  const { hasPagePermission } = usePageAndActionPermissionData();

  return computed<Nav[]>(() =>
    [
      {
        name: t('shou-ye'),
        route: '/platform',
        pageKey: PageKey.HOME,
        icon: 'home-platform',
        iconGlobal: 'home-global',
        trackEvent: TRACK_EVENT.CLICK_HOME_LEFT,
      },
      {
        name: t('zui-jin-zuo-pin'),
        route: '/home/recently',
        pageKey: PageKey.RECENTLY_WORKS,
        icon: 'home-recently',
        iconGlobal: 'recent-opus-global',
        trackEvent: TRACK_EVENT.CLICK_RECENTLY_WORK,
      },
      {
        name: t('wo-de-shou-cang'),
        route: '/home/collection',
        pageKey: PageKey.MY_COLLECTION,
        icon: 'home-collection',
        iconGlobal: 'collect-global',
        trackEvent: TRACK_EVENT.CLICK_COLLECT,
      },
      {
        name: t('ge-ren-kong-jian'),
        route: '/home/personal',
        pageKey: PageKey.PERSONAL_SPACE,
        icon: 'home-personal',
        iconGlobal: 'personal-space-global',
        trackEvent: TRACK_EVENT.CLICK_PERSONAL_SPACE,
      },
      {
        name: t('gong-xiang-kong-jian'),
        route: '/home/team',
        pageKey: PageKey.TEAM_SPACE,
        icon: 'home-shared',
        iconGlobal: 'share-space-global',
        crown: true,
        trackEvent: TRACK_EVENT.CLICK_TEAM_SPACE,
      },
      {
        name: t('pin-pai-gui-fan'),
        route: '/home/brand',
        pageKey: PageKey.BRAND_MANAGE,
        icon: 'home-brand',
        iconGlobal: 'brand-kit-global',
        crown: true,
        trackEvent: TRACK_EVENT.CLICK_BRAND_KIT,
      },
      {
        name: 'lab实验室',
        route: '/home/lab',
        pageKey: PageKey.LAB,
        icon: 'lab',
        iconGlobal: 'lab-fill',
        trackEvent: TRACK_EVENT.CLICK_BRAND_KIT,
      },
    ].filter(item => hasPagePermission(item.pageKey)),
  );
};

export const useCenterNav = () => {
  const { t: $t } = getI18nService();
  const { isTeamRight } = useDetector();
  const userInfo = useUserInfo();
  const { hasPagePermission, hasActionPermission } = usePageAndActionPermissionData();

  return computed<Nav[]>(() =>
    [
      {
        name: $t('zhang-hao-she-zhi'),
        route: '/workspace/center/account',
        pageKey: PageKey.CENTER_ACCOUNT,
        icon: 'user-outline',
        iconGlobal: 'user-outline',
        workspaceIcon: {
          icon: IconAccount,
          iconFill: IconAccountFill,
        },
      },
      {
        name: $t('tuan-dui-guan-li'),
        route: '/workspace/center/member',
        pageKey: PageKey.CENTER_MEMBER,
        icon: 'user-group-outline',
        iconGlobal: 'user-group-outline',
        rights_type: [
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.SUPER_ADMIN,
          USER_ADMIN.MANAGEMENT_PLATFORM,
        ],
        workspaceIcon: {
          icon: IconOrganization,
          iconFill: IconOrganizationFill,
        },
      },
      {
        name: $t('xiao-hao-ji-lu'),
        route: '/workspace/center/consume',
        pageKey: PageKey.CENTER_CONSUME,
        icon: 'user-cost',
        iconGlobal: 'user-cost',
        rights_type: [
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
          USER_LEVEL_KEY.PERSONAL_LITE,
        ],
        permission: [USER_ADMIN.NORMAL],
        workspaceIcon: {
          icon: IconConsume,
          iconFill: IconConsumeFill,
        },
      },
      {
        name: $t('quan-yi-guan-li'),
        route: '/workspace/center/equity',
        pageKey: PageKey.CENTER_EQUITY,
        icon: 'user-benefit',
        iconGlobal: 'user-benefit',
        rights_type: [
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
          USER_LEVEL_KEY.PERSONAL_LITE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.SUPER_ADMIN,
          USER_ADMIN.MANAGEMENT_PLATFORM,
        ],
        workspaceIcon: {
          icon: IconEquity,
          iconFill: IconEquityFill,
        },
      },
      {
        name: $t('ding-dan-fa-piao'),
        route: '/workspace/center/order',
        pageKey: PageKey.CENTER_ORDER,
        icon: 'user-order',
        iconGlobal: 'user-order',
        rights_type: [
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
          USER_LEVEL_KEY.PERSONAL_LITE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.SUPER_ADMIN,
          USER_ADMIN.MANAGEMENT_PLATFORM,
        ],
        workspaceIcon: {
          icon: IconOrder,
          iconFill: IconOrderFill,
        },
      },
      {
        name: isTeamRight.value ? $t('tuan-dui-zhu-ye') : $t('ge-ren-zhu-ye'),
        route: `/personal-page/${userInfo.value?.organization_id}.html`,
        pageKey: PageKey.PERSONAL_PAGE,
        icon: 'homepage',
        iconGlobal: 'homepage',
      },
    ].filter(item => {
      if (!hasPagePermission(item.pageKey)) {
        return false;
      }

      if (item.pageKey === PageKey.PERSONAL_PAGE) {
        if (
          isTeamRight.value &&
          userInfo.value?.permission === USER_ADMIN.NORMAL &&
          !hasActionPermission(PAGE_ACTION_KEYS.PERSONAL_PAGE_MEMBER_VIEW)
        ) {
          return false;
        }
      }

      return true;
    }),
  );
};
